<script setup>
// M26.5 / M26.5.1 — premium identity card for every workforce unit. The
// parent normalizes every source (company coordinator, project employees,
// staff supervisor, conceptual registry roles) into one `unit` shape so this
// component stays purely presentational. Every metric is real-or-—
// (workforceFormat.js) — never a fabricated 0.
//
// M26.5.1 Part 1/3/10 — hierarchy reordered to read as an employee, not a
// stats table: Avatar/Name/Powered by -> Department + Role + live Status ->
// Currently Working On -> Capabilities -> Knowledge/Memory -> a single
// compact Metrics row -> footer. Queue/Active (raw task-queue counts) are
// folded into the status badge + Currently Working On rather than shown as
// their own big tiles, since they're the same underlying signal restated.
import { computed } from 'vue';
import GeneratedAvatar from 'dashboard/components-next/avatar/GeneratedAvatar.vue';
import CapabilityGroups from './CapabilityGroups.vue';
import LiveStatusBadge from './LiveStatusBadge.vue';
import { departmentColor } from './departmentColors';
import { fmt, fmtMs, relativeTime } from './workforceFormat';

const props = defineProps({
  unit: {
    type: Object,
    required: true,
    // { key, kind, name, poweredBy, roleLabel, department, projectName,
    //   status, liveStatus, health, busy, capabilities[], knowledgePercent,
    //   memoryPercent, leadsManaged, aiReplies, documentsShared, siteVisits,
    //   queueCount, activeCount, avgResponseMs, lastActivity, workloadPct,
    //   currentlyWorkingOn[], quickActionLabel, quickActionIcon }
  },
});

const emit = defineEmits(['quickAction', 'open']);

const HEALTH_META = {
  healthy: {
    label: 'Healthy',
    dot: 'bg-success-500',
    text: 'text-success-700 dark:text-success-400',
  },
  degraded: {
    label: 'Degraded',
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400',
  },
  attention: {
    label: 'Needs attention',
    dot: 'bg-ruby-500',
    text: 'text-ruby-700 dark:text-ruby-400',
  },
};

const color = computed(() => departmentColor(props.unit.department));
const healthMeta = computed(
  () => HEALTH_META[props.unit.health] || HEALTH_META.healthy
);

const coreMetricTiles = computed(() => [
  { label: 'Leads', value: fmt(props.unit.leadsManaged) },
  { label: 'Replies', value: fmt(props.unit.aiReplies) },
  { label: 'Documents', value: fmt(props.unit.documentsShared) },
  { label: 'Visits', value: fmt(props.unit.siteVisits) },
]);
// M26.5.2 polish — units without any real per-unit metric (most conceptual
// registry roles) used to show 4 literal "—" tiles. Hide the whole footer
// section instead when there is truly nothing real to show there.
const hasCoreMetrics = computed(() =>
  coreMetricTiles.value.some(tile => tile.value !== '—')
);
const hasMetricsFooter = computed(
  () =>
    hasCoreMetrics.value ||
    props.unit.avgResponseMs != null ||
    props.unit.extraKpis?.length
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="flex flex-col h-full rounded-xl border border-n-weak bg-n-background overflow-hidden cursor-pointer transition-all duration-200 hover:border-n-strong hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
    role="button"
    tabindex="0"
    @click="emit('open', unit)"
    @keydown.enter="emit('open', unit)"
  >
    <!-- Identity header: Avatar -> Name -> Powered by -->
    <div class="flex items-start gap-2.5 px-3.5 pt-3.5">
      <GeneratedAvatar
        :name="unit.name"
        :color="color.hex"
        :variant="unit.department"
        :seed="unit.key"
        :size="40"
      />
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-n-slate-12 truncate leading-tight">
          {{ unit.name }}
        </p>
        <p v-if="unit.poweredBy" class="text-[11px] text-n-slate-8 truncate">
          Powered by {{ unit.poweredBy }}
        </p>
      </div>
    </div>

    <!-- Department -> Role -> Status -->
    <div class="flex items-center gap-1.5 px-3.5 pt-2 flex-wrap">
      <span
        class="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
        :class="[color.bgClass, color.textClass]"
      >
        {{ color.label }}
      </span>
      <span class="text-[11px] text-n-slate-7 truncate">{{
        unit.roleLabel
      }}</span>
      <LiveStatusBadge :live-status="unit.liveStatus" class="ml-auto" />
    </div>

    <!-- Currently Working On — the biggest M26.5.1 addition: real, per-lead -->
    <div class="px-3.5 pt-3">
      <p
        class="text-[10px] font-semibold text-n-slate-7 uppercase tracking-wider mb-1.5"
      >
        Currently Working On
      </p>
      <ul v-if="unit.currentlyWorkingOn?.length" class="flex flex-col gap-1">
        <li
          v-for="lead in unit.currentlyWorkingOn"
          :key="lead.lead_id"
          class="flex items-center justify-between gap-2 text-[11px]"
        >
          <span class="text-n-slate-11 font-medium truncate">{{
            lead.name
          }}</span>
          <span class="text-n-slate-7 truncate">{{ lead.status_label }}</span>
        </li>
      </ul>
      <p v-else class="text-[11px] text-n-slate-6">No active conversations.</p>
    </div>

    <!-- Capabilities, grouped -->
    <div v-if="unit.capabilities?.length" class="px-3.5 pt-3">
      <CapabilityGroups :capabilities="unit.capabilities" compact />
    </div>

    <!-- Knowledge / Memory — de-emphasized, small -->
    <div
      v-if="
        unit.knowledgePercent !== undefined || unit.memoryPercent !== undefined
      "
      class="grid grid-cols-2 gap-2 px-3.5 pt-3"
    >
      <div>
        <div class="flex items-center justify-between mb-1">
          <p class="text-[10px] text-n-slate-7">Knowledge</p>
          <p class="text-[10px] text-n-slate-7 tabular-nums">
            {{ fmt(unit.knowledgePercent, '%') }}
          </p>
        </div>
        <div class="h-1 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full bg-woot-500"
            :style="{ width: `${unit.knowledgePercent || 0}%` }"
          />
        </div>
      </div>
      <div>
        <div class="flex items-center justify-between mb-1">
          <p class="text-[10px] text-n-slate-7">Memory</p>
          <p class="text-[10px] text-n-slate-7 tabular-nums">
            {{ fmt(unit.memoryPercent, '%') }}
          </p>
        </div>
        <div class="h-1 rounded-full bg-n-alpha-2 overflow-hidden">
          <div
            class="h-full rounded-full bg-woot-500"
            :style="{ width: `${unit.memoryPercent || 0}%` }"
          />
        </div>
      </div>
    </div>

    <div class="flex-1" />

    <!-- Compact Metrics row — core 4 tiles + response time + real extras,
         Queue/Active/Workload intentionally folded into the status badge and
         Currently Working On above rather than repeated as separate tiles.
         Hidden entirely for units with nothing real to show here. -->
    <div
      v-if="hasMetricsFooter"
      class="px-3.5 pt-3 mt-3 border-t border-n-weak"
    >
      <div v-if="hasCoreMetrics" class="grid grid-cols-4 gap-y-1 text-center">
        <div v-for="tile in coreMetricTiles" :key="tile.label">
          <p class="text-sm font-bold text-n-slate-12 tabular-nums">
            {{ tile.value }}
          </p>
          <p class="text-[10px] text-n-slate-7">{{ tile.label }}</p>
        </div>
      </div>
      <div
        v-if="unit.avgResponseMs != null"
        class="flex items-center justify-between text-[11px]"
        :class="hasCoreMetrics ? 'pt-2' : ''"
      >
        <span class="text-n-slate-7">Avg Response</span>
        <span class="text-n-slate-11 font-medium tabular-nums">{{
          fmtMs(unit.avgResponseMs)
        }}</span>
      </div>
      <div
        v-if="unit.extraKpis?.length"
        class="flex flex-wrap gap-1"
        :class="hasCoreMetrics || unit.avgResponseMs != null ? 'pt-2' : ''"
      >
        <span
          v-for="kpi in unit.extraKpis"
          :key="kpi.label"
          class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-8"
        >
          <b class="text-n-slate-11">{{ kpi.value }}</b> {{ kpi.label }}
        </span>
      </div>
    </div>

    <!-- Last real activity -->
    <p
      v-if="unit.lastActivity"
      class="px-3.5 pt-2.5 text-[11px] text-n-slate-7 truncate"
    >
      <span class="i-lucide-history size-3 inline-block align-[-1px] mr-1" />
      {{ unit.lastActivity.label }} · {{ relativeTime(unit.lastActivity.at) }}
    </p>

    <!-- Footer: health + quick action -->
    <div
      class="flex items-center justify-between gap-2 px-3.5 py-2.5 mt-3 border-t border-n-weak"
    >
      <span
        class="flex items-center gap-1.5 text-[11px] font-medium"
        :class="healthMeta.text"
      >
        <span class="size-1.5 rounded-full" :class="healthMeta.dot" />
        {{ healthMeta.label }}
      </span>
      <button
        class="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-n-weak text-[11px] font-medium text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors disabled:opacity-40"
        :disabled="unit.busy"
        @click.stop="emit('quickAction', unit)"
      >
        <span :class="unit.quickActionIcon" class="size-3" />
        {{ unit.quickActionLabel }}
      </button>
    </div>
  </div>
</template>
